import { useCallback, useState } from 'react';
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { getGarageClient } from '../index';
import { trackEvent } from '../../utils/analytics';

export type QueryGetZipCodeInfoArgsType = {
  zipcode: string;
};

type GetZipCodeInfoInput = QueryGetZipCodeInfoArgsType;

export type ZipCodeInfoType = {
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  state?: string;
};

type GetZipCodeInfoOutput = {
  getZipCodeInfo: ZipCodeInfoType;
};

/**
 * Query zip code info.
 */
export const GET_ZIP_CODE_INFO: TypedDocumentNode<GetZipCodeInfoOutput, GetZipCodeInfoInput> = gql`
  query QueryZipCodeInfo($zipcode: String!) {
    getZipCodeInfo(zipcode: $zipcode) {
      city
      state
      latitude
      longitude
    }
  }
`;

export const ZIP_LENGTH = 5;

/**
 * Returns a function to validate zip code info
 */
export const useValidateZipCode = (): [boolean, (zipCode: string) => Promise<boolean | string>] => {
  const client = getGarageClient();
  const [isLoading, setIsLoading] = useState(false);

  const getZipCodeInfo = useCallback(
    async (zipCode: string): Promise<boolean | string> => {
      try {
        if (zipCode.length !== ZIP_LENGTH) {
          return `ZIP code should have ${ZIP_LENGTH} digits`;
        }
        setIsLoading(true);
        const { error, data } = await client.query({
          query: GET_ZIP_CODE_INFO,
          variables: {
            zipcode: zipCode,
          },
        });
        setIsLoading(false);
        if (error) {
          void trackEvent('GarageOnlyAPI', { eventLabel: 'ZipCode', eventMetadata: { err: error } });
        }
        const isCorrect = !!data?.getZipCodeInfo.city && !!data?.getZipCodeInfo.state;
        if (isCorrect) {
          return true;
        }
        return 'Invalid ZIP code. Please check it.';
      } catch (error) {
        setIsLoading(false);
        return 'Validation failed. Please try again.';
      }
    },
    [client, setIsLoading]
  );

  return [isLoading, getZipCodeInfo];
};
